/**
 * EmailPreviewModal — shared preview dialog used by Emails, Reminders, and
 * Acknowledgments tabs on the Drawdown detail page.
 *
 * Calls POST /api/drawdowns/:id/emails/preview to render ONE recipient's
 * email server-side (same code path as the live send), then displays it in
 * an Outlook-style reading pane: From / To / Cc / Subject + sandboxed iframe
 * for the body. A small dropdown lets the user flip between the selected
 * recipients without closing the modal.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  X, Loader2, AlertCircle, Paperclip, Mail, ChevronLeft, ChevronRight,
  AlertTriangle, FileText, Send, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailPreviewModal = ({
  open,
  onClose,
  drawdownId,
  templateId,
  kind = 'drawdown_notice', // drawdown_notice | drawdown_reminder | drawdown_acknowledgment | soa_email
  recipients = [],          // [{ id, account_name, class_name?, folio_no? }]
  includeDrawdownNotice = false,
  bccSelf = false,
  dateFormat = 'dd mmmm yyyy',
  numberFormat = 'western',
  ccUserIds = [],
  bccUserIds = [],
  asOfDate = '',
  // Phase J overrides — let other senders (SOA emails) reuse this modal
  // without inheriting the drawdown URL paths.
  previewPath,                           // e.g. "funds/abc/soa-emails/preview"
  testSendPath,                          // e.g. "funds/abc/soa-emails/test-send"
  recipientFieldName = 'drawdown_investor_id',  // body key for the per-recipient id
  // Phase L3 — extra fields merged into both preview & test-send request
  // bodies. Used by Investor Communication to forward the chosen `fund_id`
  // so {{fund_name}} resolves correctly.
  extraPayload = {},
}) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Test-send-to-me state
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, delivered_to, attachment_count } | null
  const [testError, setTestError] = useState('');

  const activeRecipient = useMemo(() => recipients[activeIdx] || null, [recipients, activeIdx]);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    setTestResult(null);
    setTestError('');
  }, [open, templateId]);

  useEffect(() => {
    if (!open || !templateId || !activeRecipient) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setData(null);
      try {
        const url = previewPath
          ? `${API}/${previewPath}`
          : `${API}/drawdowns/${drawdownId}/emails/preview`;
        const body = {
          template_id: templateId,
          [recipientFieldName]: activeRecipient.id,
          kind,
          include_drawdown_notice: includeDrawdownNotice,
          bcc_self: bccSelf,
          date_format: dateFormat,
          number_format: numberFormat,
          cc_user_ids: ccUserIds,
          bcc_user_ids: bccUserIds,
          as_of_date: asOfDate || null,
          ...extraPayload,
        };
        const { data: res } = await axios.post(url, body);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to render preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, templateId, activeRecipient?.id, includeDrawdownNotice, bccSelf, dateFormat, numberFormat, kind, drawdownId, JSON.stringify(ccUserIds), JSON.stringify(bccUserIds), asOfDate, previewPath, recipientFieldName, JSON.stringify(extraPayload)]);

  if (!open) return null;

  const total = recipients.length;
  const goPrev = () => setActiveIdx(i => Math.max(0, i - 1));
  const goNext = () => setActiveIdx(i => Math.min(total - 1, i + 1));

  const handleTestSend = async () => {
    if (!activeRecipient || !templateId) return;
    setTestSending(true); setTestError(''); setTestResult(null);
    try {
      const url = testSendPath
        ? `${API}/${testSendPath}`
        : `${API}/drawdowns/${drawdownId}/emails/test-send`;
      const body = {
        template_id: templateId,
        [recipientFieldName]: activeRecipient.id,
        kind,
        include_drawdown_notice: includeDrawdownNotice,
        date_format: dateFormat,
        number_format: numberFormat,
        as_of_date: asOfDate || null,
        ...extraPayload,
      };
      const { data: res } = await axios.post(url, body);
      setTestResult(res);
    } catch (err) {
      setTestError(err.response?.data?.detail || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  const fromLine = data
    ? (data.from_name ? `${data.from_name} <${data.from_email || ''}>` : (data.from_email || ''))
    : '';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="email-preview-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center text-gray-900">
            <Mail size={18} className="mr-2 text-blue-600" />
            <h3 className="text-base font-bold">Email Preview</h3>
            {total > 1 && (
              <span className="ml-3 text-xs text-gray-500" data-testid="preview-counter">
                Recipient {activeIdx + 1} of {total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={activeIdx === 0}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="preview-prev"
                  title="Previous recipient"
                >
                  <ChevronLeft size={16} />
                </button>
                <select
                  value={activeIdx}
                  onChange={e => setActiveIdx(Number(e.target.value))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[260px]"
                  data-testid="preview-recipient-select"
                >
                  {recipients.map((r, i) => (
                    <option key={r.id} value={i}>
                      {r.account_name || '(unnamed)'}
                      {r.class_name ? ` · ${r.class_name}` : ''}
                      {r.folio_no ? ` · ${r.folio_no}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={activeIdx === total - 1}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="preview-next"
                  title="Next recipient"
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              data-testid="preview-close"
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-12 text-center text-gray-500" data-testid="preview-loading">
              <Loader2 size={28} className="mx-auto mb-2 animate-spin text-blue-600" />
              <p className="text-sm">Rendering preview…</p>
            </div>
          )}

          {error && (
            <div className="m-5 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start" data-testid="preview-error">
              <AlertCircle size={16} className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-0.5">Preview failed</div>
                <div>{error}</div>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <div className="p-5 space-y-3" data-testid="preview-content">
              {/* Recipient Header */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1.5">
                {fromLine && (
                  <div className="flex">
                    <span className="w-16 text-gray-500 font-medium flex-shrink-0">From</span>
                    <span className="text-gray-900 break-all" data-testid="preview-from">{fromLine}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-16 text-gray-500 font-medium flex-shrink-0">To</span>
                  <span className="text-gray-900 break-all" data-testid="preview-to">
                    {(data.to_emails || []).length > 0
                      ? data.to_emails.join(', ')
                      : <span className="text-red-600 italic">No primary contact email — this email cannot be sent</span>}
                  </span>
                </div>
                {(data.cc_emails || []).length > 0 && (
                  <div className="flex">
                    <span className="w-16 text-gray-500 font-medium flex-shrink-0">Cc</span>
                    <span className="text-gray-900 break-all" data-testid="preview-cc">{data.cc_emails.join(', ')}</span>
                  </div>
                )}
                {data.bcc_self && fromLine && (
                  <div className="flex">
                    <span className="w-16 text-gray-500 font-medium flex-shrink-0">Bcc</span>
                    <span className="text-gray-900 break-all">{data.from_email}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="w-16 text-gray-500 font-medium flex-shrink-0">Subject</span>
                  <span className="text-gray-900 font-semibold" data-testid="preview-subject">{data.subject || <em className="text-gray-400">(no subject)</em>}</span>
                </div>
              </div>

              {data.missing_recipients && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-900 flex items-start" data-testid="preview-no-recipients-warn">
                  <AlertTriangle size={14} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>This investor has no primary or additional contact emails — sending will skip them.</span>
                </div>
              )}

              {/* Attachments */}
              {(data.attachment_names || []).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="preview-attachments">
                  <div className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center">
                    <Paperclip size={12} className="mr-1.5" /> Attachments ({data.attachment_names.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.attachment_names.map((name, i) => (
                      <span
                        key={i}
                        className={`inline-flex items-center text-xs px-2 py-1 rounded border ${
                          name.startsWith('⚠')
                            ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                            : 'bg-blue-50 border-blue-200 text-blue-800'
                        }`}
                      >
                        <FileText size={11} className="mr-1" /> {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Body — sandboxed iframe so the email's CSS can't leak out */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
                  Rendered email body
                </div>
                <iframe
                  title="email-body-preview"
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#222;margin:18px;line-height:1.5}img{max-width:100%;height:auto}table{border-collapse:collapse}a{color:#1d4ed8}ul{list-style:disc outside;padding-left:1.5em;margin:0.5em 0}ol{list-style:decimal outside;padding-left:1.5em;margin:0.5em 0}ul ul{list-style:circle outside}ul ul ul{list-style:square outside}li{margin:0.15em 0}li>p{margin:0}blockquote{border-left:3px solid #cbd5e1;padding:0.25em 0.75em;margin:0.5em 0;color:#475569;font-style:italic;background:#f8fafc}h1{font-size:1.6em;font-weight:700;margin:0.6em 0 0.4em}h2{font-size:1.35em;font-weight:700;margin:0.5em 0 0.35em}h3{font-size:1.15em;font-weight:600;margin:0.4em 0 0.3em}p{margin:0.4em 0}</style></head><body>${data.body_html || ''}</body></html>`}
                  sandbox=""
                  className="w-full bg-white"
                  style={{ height: '420px', border: 0 }}
                  data-testid="preview-body-iframe"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer — test-send + close */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          {testResult && (
            <div className="mb-2 p-2 rounded text-xs bg-green-50 border border-green-200 text-green-800 flex items-center" data-testid="test-send-result">
              <CheckCircle2 size={13} className="mr-1.5 flex-shrink-0" />
              Test email delivered to <strong className="ml-1">{testResult.delivered_to}</strong>.
              {testResult.attachment_count > 0 && (
                <span className="ml-2 text-green-700">{testResult.attachment_count} attachment{testResult.attachment_count === 1 ? '' : 's'} included.</span>
              )}
            </div>
          )}
          {testError && (
            <div className="mb-2 p-2 rounded text-xs bg-red-50 border border-red-200 text-red-800 flex items-center" data-testid="test-send-error">
              <AlertCircle size={13} className="mr-1.5 flex-shrink-0" /> {testError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-gray-500 flex-1 min-w-[240px]">
              This is exactly what the recipient will see — sending isn't queued until you click <strong>Send</strong>.
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTestSend}
                disabled={!data || testSending}
                className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="test-send-btn"
                title="Deliver this rendered email to your own inbox to sanity-check the look + attachments"
              >
                {testSending ? (
                  <><Loader2 size={12} className="mr-1.5 animate-spin" /> Sending test…</>
                ) : (
                  <><Send size={12} className="mr-1.5" /> Send a test to me</>
                )}
              </button>
              <button type="button" onClick={onClose} className="text-blue-600 hover:underline text-xs" data-testid="preview-close-bottom">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreviewModal;
